import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import { cn } from "@/lib/utils";
import { AlertCircle, RefreshCw } from "lucide-react";
import { AnimatedStarburst } from "@/components/icons/AnimatedStarburst";
import { useAuthStore, ensureFreshToken } from "@/lib/store/authStore";
import { fetchAiSso } from "./fetchAiSso";

export interface LobeChatEmbedProps {
  className?: string;
  /**
   * Compact mode for the 420px right-rail panel: the ai.zaviago.com SSO bridge lands
   * straight in the chat and LobeHub serves its mobile layout (Plan 1965).
   */
  panel?: boolean;
  onReady?: () => void;
  onError?: (message: string) => void;
}

export interface LobeChatEmbedRef {
  isReady: boolean;
}

/**
 * LobeChatEmbed — iframe wrapper for self-hosted LobeChat (ai.zaviago.com).
 *
 * Auth: cross-origin SSO, same pattern as Chatwoot/Ghost. We fetch a one-time
 * SSO URL from Control Plane (control_plane.api.ai.get_sso_url), which the
 * ai.zaviago.com shim exchanges for a per-user session cookie. The tenant is
 * derived from the logged-in user's JWT — never hardcoded — so every ERP tool
 * call is scoped to THIS user's business. Direct/anon access is rejected (401).
 */
const LobeChatEmbed = forwardRef<LobeChatEmbedRef, LobeChatEmbedProps>(
  function LobeChatEmbed({ className, panel = false, onReady, onError }, ref) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [status, setStatus] = useState<"loading" | "ready" | "error">(
      "loading"
    );
    const [iframeSrc, setIframeSrc] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const currentTenant = useAuthStore((state) => state.currentTenant);
    const username = useAuthStore((state) => state.username);

    useImperativeHandle(ref, () => ({
      get isReady() {
        return status === "ready";
      },
    }));

    // Fetch a fresh one-time SSO URL. Tokens are single-use, so we mint a new
    // one each time the embed (re)loads.
    const loadSso = useCallback(async () => {
      if (!currentTenant || !username) {
        setStatus("error");
        setErrorMsg("Missing tenant or user information");
        return;
      }
      setStatus("loading");
      setErrorMsg(null);
      try {
        // Avoid the token-refresh race (frappe-debug #62): wait for any
        // in-flight CP token refresh before requesting the SSO URL.
        await ensureFreshToken();
        const data = await fetchAiSso(currentTenant, username);
        const url = new URL(data.url);
        if (panel) url.searchParams.set("panel", "1");
        setIframeSrc(url.toString());
      } catch (err) {
        setStatus("error");
        const msg =
          err instanceof Error ? err.message : "Failed to load AI assistant";
        setErrorMsg(msg);
        onError?.(msg);
      }
    }, [currentTenant, username, panel, onError]);

    useEffect(() => {
      void loadSso();
    }, [loadSso]);

    const handleIframeLoad = useCallback(() => {
      setStatus("ready");
      onReady?.();
    }, [onReady]);

    const handleIframeError = useCallback(() => {
      setStatus("error");
      setErrorMsg("Failed to load AI assistant");
      onError?.("Failed to load AI assistant");
    }, [onError]);

    const handleRetry = useCallback(() => {
      void loadSso();
    }, [loadSso]);

    return (
      <div className={cn("relative h-full w-full", className)}>
        {/* Loading state */}
        {status === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-background z-10">
            <AnimatedStarburst />
            <p className="text-sm text-muted-foreground">
              กำลังเปิด AI Assistant...
            </p>
          </div>
        )}

        {/* Error state */}
        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-background z-10">
            <AlertCircle className="h-10 w-10 text-destructive" />
            <p className="text-sm text-muted-foreground">
              {errorMsg || "ไม่สามารถเชื่อมต่อ AI Assistant ได้"}
            </p>
            <button
              onClick={handleRetry}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
            >
              <RefreshCw className="h-4 w-4" />
              ลองอีกครั้ง
            </button>
          </div>
        )}

        {/* LobeChat iframe — only mounts once we have an SSO URL */}
        {iframeSrc && (
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            className="absolute inset-0 w-full h-full border-none"
            title="Zaviago AI Assistant"
            allow="clipboard-read; clipboard-write; microphone"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        )}
      </div>
    );
  }
);

export default LobeChatEmbed;
